import { useMemo } from 'react'

import { createPublicClient, defineChain, http } from 'viem'
import {
    arbitrum,
    avalanche,
    base,
    blast,
    bsc,
    celo,
    mainnet,
    megaethTestnet,
    mode,
    monadTestnet,
    opBNB,
    optimism,
    polygon,
    scroll,
    sepolia,
} from 'viem/chains'

import { ChainId } from '@src/configs/chainInfo'
import { isFeatureEnabled } from '@src/utils'

// Manually define unichain chain
export const unichain = defineChain({
    id: 130,
    name: 'Unichain',
    nativeCurrency: { name: 'Ether', symbol: 'ETH', decimals: 18 },
    rpcUrls: {
        default: {
            http: ['https://unichain-mainnet.g.alchemy.com/v2/yHZvr09DRycjGM-t-j5hkUvOgfgx8gbt'],
        },
    },
    blockExplorers: {
        default: {
            name: 'Uniscan',
            url: 'https://uniscan.xyz',
        },
    },
    contracts: {},
    sourceId: 1,
})

// Define a mapping of Chain IDs to Viem chains for testnet chains
const testnetViemChainConfigs: Record<number, any> = {
    [ChainId.Sepolia]: sepolia,
    [ChainId.MonadTestnet]: monadTestnet,
    [ChainId.MegaEthTestnet]: megaethTestnet,
}

// Define a mapping of chain IDs to Viem chain configurations
export const viemChainConfigs: Record<number, any> = isFeatureEnabled('USE_TESTNETS_ONLY')
    ? testnetViemChainConfigs
    : {
          1: mainnet,
          10: optimism,
          137: polygon,
          42161: arbitrum,
          43114: avalanche,
          42220: celo,
          8453: base,
          81457: blast,
          56: bsc,
          204: opBNB,
          534352: scroll,
          34443: mode,
          130: unichain,
          6342: megaethTestnet,
      }

export const getViemClient = (chainId: number) => {
    const chainConfig = viemChainConfigs[chainId]

    if (!chainConfig) {
        console.error(`[getViemClient] Unsupported chainId: ${chainId}`)
        return void 0
    } else {
        return createPublicClient({
            chain: chainConfig,
            transport: http(),
        })
    }
}
/**
 * Custom hook to get the Viem public client based on a chain ID.
 * @param chainId - The chain ID for which to get the client.
 * @returns The Viem public client for the specified chain.
 */
export const useViemClient = (chainId: number) => {
    const publicClient = useMemo(() => {
        return getViemClient(chainId)
    }, [chainId])

    return publicClient
}
